(ns ui.captions.query
  "Pure lookups in the editor2 state for the captions sidebar and its
   action handlers. Kept apart from `ui.captions.actions`, which
   requires `ui.editor2.actions`: the sidebar is required by
   `ui.editor2.sidebar`, which the actions namespace requires itself.

   The transcript hangs on the media asset (`:asset/words`, times
   relative to the media). A video clip with `:clip/captions` shows the
   words of the range `[:clip/offset, :clip/offset + duration]` of its
   asset, so every clip of the media shows its own range and a
   corrected word is corrected in all of them."
  (:require [app.captions.words :as words]
            [datascript.core :as ds]
            [ui.editor2.common :as common]))

(defn media-clip?
  "The clips that can carry captions."
  [clip]
  (= :video (get-in clip [:clip/asset :asset/type])))

(defn captioned?
  [clip]
  (some? (:clip/captions clip)))

(defn style
  [clip]
  (get-in clip [:clip/captions :captions/style]))

(defn media-clips
  [state]
  (filter media-clip?
          (common/get-all-clips state)))

(defn captioned-clips
  [state]
  (filter captioned?
          (media-clips state)))

(defn source-clip
  "The clip the sidebar works on: the one picked in the sidebar, else
   the selected video clip, else the first one."
  [state]
  (let [db (get-in state [:editor2 :db])
        entity (fn [clip-uuid]
                 (when clip-uuid
                   (ds/entity db [:clip/uuid clip-uuid])))
        picked (entity (get-in state [:editor2 :captions-sidebar :source-clip]))
        selected (entity (get-in state [:editor2 :selected-clip]))]
    (cond
      (and picked (media-clip? picked)) picked
      (and selected (media-clip? selected)) selected
      :else (first (media-clips state)))))

(defn transcript
  "The words of a media asset in order."
  [media-asset]
  (sort-by (juxt :word/start :word/index)
           (:asset/words media-asset)))

(defn clip-range
  "The range of the media that the clip shows."
  [clip]
  {:offset-ms (or (:clip/offset clip) 0)
   :duration-ms (- (:clip/end clip)
                   (:clip/start clip))})

(defn clip-word-entities
  "The word entities of the media that fall into the clip's range, with
   their times as stored (relative to the media)."
  [clip]
  (filter (partial words/in-range? (clip-range clip))
          (transcript (:clip/asset clip))))

(defn clip-stored-words
  "The words of the clip as plain maps, which is what the transcript
   editor shows and what its edits are compared with."
  [clip]
  (mapv words/word-map
        (clip-word-entities clip)))

(defn media-range
  "The part of the media that the clip shows as `{:lo :hi}`, the
   bounds for the times of new words."
  [clip]
  (let [{:keys [offset-ms duration-ms]} (clip-range clip)]
    {:lo offset-ms
     :hi (+ offset-ms duration-ms)}))

(defn source-url
  "The URL of the clip's media that the server can download: the
   temporary upload URI of a media uploaded in this session, or the
   `/blob/` (or http) URI of a saved one. nil while an upload is still
   running, when the asset only has the browser's `blob:` object URL
   (see `ui.openrouter.modal/asset-source-url`)."
  [clip]
  (let [asset (:clip/asset clip)
        uri (:asset/uri asset)]
    (or (:asset/uploaded-uri asset)
        (when (and (string? uri)
                   (or (.startsWith uri "/blob/")
                       (.startsWith uri "http")))
          uri))))

(defn media-time
  "The media time of the playhead for a clip."
  [state clip]
  (+ (- (get-in state [:editor2 :playhead-position] 0)
        (:clip/start clip))
     (or (:clip/offset clip) 0)))
